import { chakra, Container, Spinner } from '@chakra-ui/react';
import { ReactNode, useContext, useEffect, useState } from 'react';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import { MarketplaceContext } from 'src/contexts/marketplace';
import Script from 'next/script';
import { MarketplaceInfo } from 'src/api/v1-api';
import { ErrorBoundary } from 'src/components/system';
import { useAuth } from 'src/utils/auth';
import PreviewModeBanner from 'src/components/atoms/preview-mode-banner';
import PreviewMessage from 'src/components/atoms/preview-message';
import PreviewLaunchChecklist from 'src/components/organisms/preview-launch-checklist';
import HelpButton from 'src/components/atoms/help-button';
import { Announcement } from 'src/components/molecules';
import { CONTAINER_MAX_WIDTH_1 } from 'src/constants/ui';
import { useRouter } from 'next/router';
import { useLayoutBanner } from 'src/contexts/layoutBanner';

const DefaultHeader = dynamic(() => import('./header'), {
  loading: () => <Spinner />,
});

const DefaultFooter = dynamic(() => import('./footer'), {
  loading: () => <Spinner />,
});

interface Props {
  header?: ReactNode;
  footer?: ReactNode;
  children: ReactNode;
  marketplace: MarketplaceInfo;
}

const DefaultLayout = ({ header, footer, children, marketplace, ...rest }: Props): JSX.Element => {
  const { marketplaceSlug, apexDomain, isAffiliate } = useContext(MarketplaceContext);
  const { isAuthenticated, hasAdmin, hasStaff, authLoading, userLoaded } = useAuth();
  const { topBanner } = useLayoutBanner();
  const router = useRouter();
  const [announcementOpened, setAnnouncementOpened] = useState(true);

  useEffect(() => {
    // Create a subdomain cookie and set its value to the subdomain
    if (marketplaceSlug) {
      document.cookie = `subdomain=${marketplaceSlug}; path=/; samesite=lax;`;
    }
    if (apexDomain) {
      document.cookie = `apexdomain=${apexDomain}; path=/; samesite=lax;`;
    }
  }, [marketplaceSlug, apexDomain]);

  const isAdmin = hasAdmin(marketplace?.id);
  const isStaffUser = hasStaff();
  const previewAllowed = isAuthenticated && userLoaded && (isAdmin || isStaffUser);
  const previewAccessLoading =
    marketplace?.status === 'preview' && (authLoading || (isAuthenticated && !userLoaded));
  const showContent = !marketplace || marketplace?.status === 'active' || previewAllowed;

  const isAdminPage = router.pathname.includes('/admin');
  const showAnnouncement = marketplace?.functional?.announcement && announcementOpened;

  return (
    <>
      <Head>
        <link
          rel="icon"
          type="image/png"
          sizes="48x48"
          href={marketplace?.favicon_image || '/marketplace.ico'}
        />
        <link rel="shortcut icon" href={marketplace?.favicon_image || '/marketplace.ico'} />
        <link
          rel="apple-touch-icon"
          sizes="48x48"
          href={marketplace?.favicon_image || '/marketplace.ico'}
        />
        <meta name="apple-mobile-web-app-title" content={marketplace?.name} />
      </Head>
      {marketplace?.status === 'active' && marketplace?.functional?.google_tag_manager_id && (
        <>
          <Script
            strategy="afterInteractive"
            src={`https://www.googletagmanager.com/gtm.js?id=${marketplace.functional.google_tag_manager_id}&l=loma-marketplace`}
          />
          <Script
            id="gtm-init"
            strategy="afterInteractive"
            dangerouslySetInnerHTML={{
              __html: `
                (function(w,l){
                  w[l]=w[l]||[];
                  w[l].push({'gtm.start': new Date().getTime(),event:'gtm.js'});
                })(window,'loma-marketplace');
              `,
            }}
          />
        </>
      )}
      {marketplace?.status === 'active' && marketplace?.functional?.google_analytics_id && (
        <>
          <Script
            strategy="afterInteractive"
            src={`https://www.googletagmanager.com/gtag/js?id=${marketplace.functional.google_analytics_id}`}
          />
          <Script
            id="_gtag-init"
            strategy="afterInteractive"
            dangerouslySetInnerHTML={{
              __html: `
              window.gaTrackingId = '${marketplace.functional.google_analytics_id}';
              window.dataLayer = window.dataLayer || [];
              function gtag(){dataLayer.push(arguments);}
              gtag('js', new Date());
              gtag('config', '${marketplace.functional.google_analytics_id}', {
                page_path: window.location.pathname
              });`,
            }}
          />
        </>
      )}
      {header || <DefaultHeader {...rest} />}
      {topBanner}
      {!isAdminPage && showAnnouncement && (
        <Container
          maxW={CONTAINER_MAX_WIDTH_1}
          pt={{ base: '8px', lg: '12px' }}
          pb={topBanner ? { base: '8px', lg: '12px' } : 0}
        >
          <Announcement
            text={marketplace?.functional?.announcement ?? ''}
            onClose={() => setAnnouncementOpened(false)}
          />
        </Container>
      )}
      <chakra.main px={0}>
        {showContent && marketplace?.status === 'preview' && isAffiliate !== null && (
          <>
            <PreviewModeBanner
              launchPreviewRequested={Boolean(marketplace?.launch_preview_requested)}
              variant="default"
              isAffiliate={isAffiliate}
            />
            {!isAffiliate && <PreviewLaunchChecklist marketplace={marketplace} />}
          </>
        )}
        <ErrorBoundary>
          {previewAccessLoading ? (
            <chakra.div display="flex" justifyContent="center" py={12}>
              <Spinner />
            </chakra.div>
          ) : showContent ? (
            children
          ) : (
            <PreviewMessage />
          )}
        </ErrorBoundary>
      </chakra.main>
      {footer || <DefaultFooter {...rest} />}
      {isAdmin && <HelpButton />}
    </>
  );
};

export default DefaultLayout;
